import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    CounterBadge,
    Field,
    InfoLabel,
    Input,
    makeStyles,
    Text,
} from '@fluentui/react-components'
import { LineItemGroupDialogProps } from './LineItemGroupDialog'
import RatePanel from './RatePanel'
import LineItemPanel from './LineItemPanel'
import { Stack } from '@fluentui/react'
import { useFormikContext } from 'formik'
import {
    AccountingId,
    ConsolidationType,
    InvoiceMutationDto,
    LineItemGroupType,
} from '../../../../../../config/finance/invoiceConfig'
import SingleUnitDimensionFields from '../../../../Accounting/SingleUnitDimensionFields'
import { DimensionsDto } from '../../../../Accounting/DimensionFields'
import BaseCombobox from '../../../../../Combobox/BaseCombobox'
import EnumCombobox from '../../../../../Combobox/EnumCombobox'
import FormContainer from '../../../../FormContainer'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    accordion: {
        '& .fui-AccordionHeader__button, & .fui-AccordionPanel': {
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
})

export function Body(props: LineItemGroupDialogProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { values } = useFormikContext<InvoiceMutationDto>()
    const descriptionRef = useRef<HTMLInputElement>()

    useEffect(() => {
        descriptionRef.current?.focus()
    }, [descriptionRef.current])

    const template = useMemo(
        () => values.templateId && props.templates?.find(template => template.id === values.templateId),
        [values.templateId],
    )
    const templateGroup = useMemo(
        () =>
            props.values.templateGroupId &&
            template?.lineItemGroups?.find(group => group.id === props.values.templateGroupId),
        [props.values.templateGroupId],
    )

    useEffect(() => {
        if (templateGroup) {
            props.setValues({
                ...props.values,
                accounting: templateGroup.accounting,
                consolidation: templateGroup.consolidation,
                showStatement: templateGroup.showStatement,
            })
        }
    }, [templateGroup])

    return (
        <Accordion
            multiple
            collapsible
            defaultOpenItems={['general', 'rate', 'line-items']}
            className={styles.accordion}
        >
            <AccordionItem value="general">
                <AccordionHeader>{translate('base.table.header.description')}</AccordionHeader>
                <AccordionPanel>
                    <FormContainer>
                        <Field label={translate('invoice.edit.form.invoicegroup.general.label')}>
                            <Input
                                ref={descriptionRef}
                                onFocus={event => event.target.select()}
                                value={props.values.description}
                                placeholder={translate('base.table.header.description')}
                                onChange={(_, { value }) => props.setProperty('description', value)}
                            />
                        </Field>
                        {values.unit && props.values.isNew && (
                            <Field
                                label={{
                                    children: (
                                        <InfoLabel
                                            info={translate('invoice.edit.form.invoicegroup.general.template.tooltip')}
                                        >
                                            {translate('invoice.edit.form.invoicegroup.general.template')}
                                        </InfoLabel>
                                    ),
                                }}
                            >
                                <BaseCombobox<LineItemGroupType>
                                    entities={template?.lineItemGroups}
                                    selectedOptions={[props.values.templateGroupId]}
                                    getEntityValue={entity => entity.id}
                                    getEntityLabel={entity => entity.description}
                                    onOptionSelect={(_, data) => {
                                        props.setProperty('templateGroupId', data.selectedOptions?.[0])
                                    }}
                                />
                            </Field>
                        )}
                    </FormContainer>
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="rate">
                <AccordionHeader>{translate('invoice.edit.form.invoicegroup.rate.label')}</AccordionHeader>
                <RatePanel {...props} />
            </AccordionItem>
            <AccordionItem value="line-items">
                <AccordionHeader>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's1' }}>
                        <Text>{translate('invoice.edit.form.invoicegroup.lineitems')}</Text>
                        <CounterBadge size="small" count={props.values.lineItems.length} showZero />
                    </Stack>
                </AccordionHeader>
                <LineItemPanel {...props} />
            </AccordionItem>
            <AccordionItem value="accounting">
                <AccordionHeader>{translate('invoice.edit.form.invoicegroup.accounting')}</AccordionHeader>
                <AccordionPanel>
                    <SingleUnitDimensionFields
                        values={props.values.accounting}
                        setValue={(property: keyof DimensionsDto, item: AccountingId) =>
                            props.setProperty('accounting', {
                                ...props.values.accounting,
                                [property]: item,
                            })
                        }
                        unitKey={values.unit}
                    />
                    <Field label="Clustering">
                        <EnumCombobox<ConsolidationType>
                            entities={[
                                ConsolidationType.NONE,
                                ConsolidationType.DIMENSIONS,
                                ConsolidationType.PERIOD,
                                ConsolidationType.BOTH,
                            ]}
                            labelKeyPrefix={'base.consolidationtype'}
                            selectedOptions={[props.values.consolidation]}
                            onOptionSelect={(_, data) =>
                                props.setProperty('consolidation', data.selectedOptions?.[0] as ConsolidationType)
                            }
                        />
                    </Field>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    )
}
